import React, { useRef, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Button,
	Typography,
} from '@mui/material';
import { AddPhotoAlternateRounded, CheckCircleRounded } from '@mui/icons-material';
import { apiErrorMessage } from '../../../api/catalog';
import {
	ModalActions,
	ImagePickerArea,
	ImagePreviewWrapper,
	ImageRule,
	ImageClearButton,
	FormError,
	ColorField,
	ColorSwatch,
} from './styles';

const DEFAULT_COLOR = '#1565C0';

export interface IDepartamentoFormData {
	name: string;
	description: string;
	imageUrl?: string | null;
	imageFile?: File | null;
	color: string;
}

export interface ISimpleFormData {
	name: string;
}

type AnadirElementoModalProps = {
	open: boolean;
	onClose: () => void;
} & (
	| {
			tipo: 'departamento';
			onConfirmar: (data: IDepartamentoFormData) => void | Promise<void>;
			initialData?: IDepartamentoFormData;
	  }
	| {
			tipo: 'academia';
			onConfirmar: (data: ISimpleFormData) => void | Promise<void>;
			initialData?: ISimpleFormData;
	  }
	| {
			tipo: 'materia';
			onConfirmar: (data: ISimpleFormData) => void | Promise<void>;
			initialData?: ISimpleFormData;
	  }
);

const TITULO_ADD: Record<string, string> = {
	departamento: 'Añadir nuevo departamento',
	academia: 'Añadir nueva academia',
	materia: 'Añadir nueva materia',
};

const TITULO_EDIT: Record<string, string> = {
	departamento: 'Editar departamento',
	academia: 'Editar academia',
	materia: 'Editar materia',
};

const AnadirElementoModal: React.FC<AnadirElementoModalProps> = (props) => {
	const { open, onClose } = props;
	const isEditMode = !!props.initialData;

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [color, setColor] = useState(DEFAULT_COLOR);
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const releasePreview = (url: string | null) => {
		if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
	};

	// Sync form state when dialog opens or initialData changes
	useEffect(() => {
		if (open) {
			setName(props.initialData?.name ?? '');
			setColor(
				props.tipo === 'departamento'
					? props.initialData?.color ?? DEFAULT_COLOR
					: DEFAULT_COLOR
			);
			setDescription(
				props.tipo === 'departamento'
					? (props.initialData as IDepartamentoFormData | undefined)?.description ?? ''
					: ''
			);
			const storedImage =
				props.tipo === 'departamento'
					? (props.initialData as IDepartamentoFormData | undefined)?.imageUrl
					: undefined;
			setImagePreviewUrl(
				storedImage && /^https?:\/\//i.test(storedImage) ? storedImage : null
			);
			setImageFile(null);
			if (fileInputRef.current) fileInputRef.current.value = '';
			setError(null);
			setSaving(false);
		} else {
			handleReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleImageClick = () => {
		fileInputRef.current?.click();
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			releasePreview(imagePreviewUrl);
			setImageFile(file);
			setImagePreviewUrl(URL.createObjectURL(file));
		}
	};

	const handleClearImage = () => {
		releasePreview(imagePreviewUrl);
		setImagePreviewUrl(null);
		setImageFile(null);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleConfirmar = async () => {
		const colorValid = props.tipo !== 'departamento' || /^#[0-9A-Fa-f]{6}$/.test(color);
		if (!name.trim() || !colorValid || saving) return;
		setSaving(true);
		setError(null);

		try {
			if (props.tipo === 'departamento') {
				await props.onConfirmar({
					name: name.trim(),
					description: description.trim(),
					imageUrl: imageFile ? null : imagePreviewUrl,
					imageFile,
					color,
				});
			} else {
				await props.onConfirmar({ name: name.trim() });
			}
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (saving) return;
		handleReset();
		onClose();
	};

	const handleReset = () => {
		setName('');
		setDescription('');
		setColor(DEFAULT_COLOR);
		releasePreview(imagePreviewUrl);
		setImagePreviewUrl(null);
		setImageFile(null);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const titulo = isEditMode ? TITULO_EDIT[props.tipo] : TITULO_ADD[props.tipo];

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>{titulo}</DialogTitle>

			<DialogContent>
				<TextField
					fullWidth
					label='Nombre *'
					value={name}
					onChange={(e) => setName(e.target.value)}
					margin='normal'
					autoFocus
				/>

				{props.tipo === 'departamento' && (
					<>
						<ColorField>
							<ColorSwatch
								type='color'
								value={color}
								aria-label='Color'
								onChange={(event) => setColor(event.target.value.toUpperCase())}
							/>
							<Typography variant='body2' color='text.secondary'>
								Color {color}
							</Typography>
						</ColorField>

						<TextField
							fullWidth
							label='Descripción'
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							margin='normal'
							multiline
							rows={2}
						/>

						<input
							ref={fileInputRef}
							type='file'
							accept='image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp'
							hidden
							onChange={handleImageChange}
						/>
						<ImagePickerArea hasImage={!!imagePreviewUrl} onClick={handleImageClick}>
							{imagePreviewUrl ? (
								<>
									<ImagePreviewWrapper>
										<img src={imagePreviewUrl} alt='Vista previa' />
									</ImagePreviewWrapper>
									<CheckCircleRounded color='success' fontSize='small' />
									<Typography variant='caption' color='text.secondary'>
										{imageFile
											? 'Se guardará al confirmar.'
											: 'Haz clic para cambiar la imagen'}
									</Typography>
								</>
							) : (
								<>
									<AddPhotoAlternateRounded color='action' fontSize='large' />
									<Typography variant='body2' color='text.secondary'>
										Haz clic para seleccionar una imagen
									</Typography>
								</>
							)}
						</ImagePickerArea>
						<ImageRule>
							PNG, JPG o WEBP. La imagen debe ser cuadrada. Si mide más de 300 px por lado, se
							reduce a 300 px.
						</ImageRule>
						{imagePreviewUrl && (
							<ImageClearButton type='button' onClick={handleClearImage}>
								Quitar imagen
							</ImageClearButton>
						)}
					</>
				)}
				{error && <FormError severity='error'>{error}</FormError>}
			</DialogContent>

			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant='contained'
					onClick={handleConfirmar}
					disabled={
						!name.trim() ||
						(props.tipo === 'departamento' && !/^#[0-9A-Fa-f]{6}$/.test(color)) ||
						saving
					}
				>
					{saving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Añadir'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default AnadirElementoModal;
